import React, {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useMemo,
} from 'react';

export type AddressFieldKey =
  | 'country'
  | 'subdivision'
  | 'city'
  | 'addressLine'
  | 'addressLine2'
  | 'postalCode'
  | 'streetName'
  | 'streetNumber';

type AddressFieldsContextValue = {
  fields: Record<AddressFieldKey, ReactElement | undefined>;
};

const AddressFieldsContext = createContext<
  AddressFieldsContextValue | undefined
>(undefined);

export interface AddressFieldsProviderProps {
  children: ReactNode;
  fields: Record<AddressFieldKey, ReactElement | undefined>;
}

export const AddressFieldsProvider = ({
  children,
  fields,
}: AddressFieldsProviderProps) => {
  const value = useMemo(() => ({ fields }), [fields]);
  return (
    <AddressFieldsContext.Provider value={value}>
      {children}
    </AddressFieldsContext.Provider>
  );
};

export function useAddressFields(
  key: AddressFieldKey,
): ReactElement | undefined {
  const context = useContext(AddressFieldsContext);
  if (!context) {
    throw new Error(
      'useAddressFields must be used within AddressFieldsProvider',
    );
  }
  return context.fields[key];
}
